import apiSlice from '..';
import { snackShow } from '../../../actions';

type UpdateSellerOfferPayload = {
  alternative_offer: number | null;
  starting_bid: number | null;
  reserve_price: number | null;
  vehicle_id: number;
};

export const alternativeOffersApi = apiSlice.injectEndpoints({
  endpoints: builder => ({
    buyNow: builder.mutation<void, number>({
      query: vehicleId => ({
        url: `/alternative-offers/${vehicleId}/buy`,
        method: 'POST'
      }),
      invalidatesTags: ['LiveAuctionVehicles', 'MyBids', 'MyBidsCounters', 'AuctionTypes']
    }),
    placeAlternativeBidOffer: builder.mutation<void, { vehicle_id: number; amount: number }>({
      query: ({ vehicle_id, amount }) => ({
        url: `/alternative-offers/${vehicle_id}/bids`,
        method: 'POST',
        body: { amount }
      }),
      invalidatesTags: ['MyBids', 'MyBidsCounters', 'SidePanelBids'],
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const data = await queryFulfilled;
          dispatch(snackShow({ message: 'Offer successfully placed' }));
        } catch (e) {
          dispatch(
            snackShow({
              message: 'Error while making offer. Please refresh and try again',
              type: 'error'
            })
          );
        }
      }
    }),
    adminPlaceAlternativeBidOffer: builder.mutation<void, { vehicleId: number; buyerId: number; amount: number }>({
      query: ({ vehicleId, buyerId, amount }) => ({
        url: `/alternative-offers/${vehicleId}/bids/admin`,
        method: 'POST',
        body: {
          amount,
          userId: buyerId
        }
      }),
      transformResponse: (response: any) => response.data,
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(snackShow({ message: 'The offer was placed' }));
        } catch (e) {
          dispatch(
            snackShow({
              message: 'Error while making offer',
              type: 'error'
            })
          );
        }
      }
    }),
    placeAlternativeOffer: builder.mutation<void, { vehicle_id: number; amount: number }>({
      query: ({ vehicle_id, amount }) => ({
        url: `/alternative-offers/${vehicle_id}`,
        method: 'POST',
        body: { amount }
      })
    }),
    acceptAlternativeBidOffer: builder.mutation<void, number>({
      query: vehicleId => ({
        url: `/alternative-offers/${vehicleId}/bids/accept`,
        method: 'POST'
      })
    }),
    updateSellerOffer: builder.mutation<void, UpdateSellerOfferPayload>({
      query: ({ vehicle_id, ...payload }) => ({
        url: `/alternative-offers/${vehicle_id}`,
        body: payload,
        method: 'PUT'
      })
    }),
    editAlternativeBidOffer: builder.mutation<void, { id: number; amount: number }>({
      query: ({ id, amount }) => ({
        url: `/alternative-offers/${id}`,
        body: { amount },
        method: 'PATCH'
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(snackShow({ message: 'Successfully updated offer' }));
        } catch (error) {
          dispatch(
            snackShow({
              message: (error as any)?.error?.data?.data?.message || 'Error while updating offer',
              type: 'error'
            })
          );
        }
      }
    }),
    removeAlternativeBidOffer: builder.mutation<void, number>({
      query: id => ({
        url: `/alternative-offers/${id}`,
        method: 'DELETE'
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(snackShow({ message: 'Successfully removed offer' }));
        } catch (error) {
          dispatch(
            snackShow({
              message: (error as any)?.error?.data?.data?.message || 'Error while removing offer',
              type: 'error'
            })
          );
        }
      }
    }),
    removeAllAlternativeBidOffers: builder.mutation<void, { vehicleId: number }>({
      query: ({ vehicleId }) => ({
        url: `/alternative-offers/${vehicleId}/all`,
        method: 'DELETE'
      })
    })
  }),
  overrideExisting: true
});

export const {
  useBuyNowMutation,
  usePlaceAlternativeBidOfferMutation,
  usePlaceAlternativeOfferMutation,
  useAcceptAlternativeBidOfferMutation,
  useUpdateSellerOfferMutation,
  useEditAlternativeBidOfferMutation,
  useRemoveAlternativeBidOfferMutation,
  useRemoveAllAlternativeBidOffersMutation,
  useAdminPlaceAlternativeBidOfferMutation
} = alternativeOffersApi;
export default alternativeOffersApi;
